import React, { useEffect, useState } from "react";

/**
 * Notification configuration.
 * To push a new notification:
 *   1. Set `enabled: true`
 *   2. Update title / message / ctaLabel
 *   3. Bump `id` so users who dismissed the previous one see this new one
 */
const NOTIFICATION: {
  id: string;
  enabled: boolean;
  title: string;
  message: string;
  ctaLabel: string;
  secondary: { label: string; href: string } | null;
} = {
  id: "2026-04-placeholder",
  enabled: false,
  title: "Heads up",
  message: "Placeholder notification text — update me anytime.",
  ctaLabel: "Got it",
  secondary: null,
};

const STORAGE_PREFIX = "corlmi:notif-dismissed:";

const NotificationPopup: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [forceShow, setForceShow] = useState(false);

  const [customContent, setCustomContent] = useState<{title: string, message: string} | null>(null);

  // On mount: show if enabled AND not yet dismissed this session
  useEffect(() => {
    const handleTrigger = (e: any) => {
      if (e.detail) {
        setCustomContent(e.detail);
        setOpen(true);
      }
    };

    window.addEventListener("corlmi:show-notification", handleTrigger);

    if (NOTIFICATION.enabled) {
      try {
        const dismissed = sessionStorage.getItem(STORAGE_PREFIX + NOTIFICATION.id);
        if (!dismissed) setOpen(true);
      } catch {
        setOpen(true);
      }
    }

    return () => window.removeEventListener("corlmi:show-notification", handleTrigger);
  }, []);

  // Esc to close
  useEffect(() => {
    if (!open && !forceShow) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, forceShow]);

  const handleClose = () => {
    if (forceShow) {
      // Preview mode — just hide, don't persist dismissal
      setForceShow(false);
      return;
    }
    setOpen(false);
    setCustomContent(null);
    try {
      sessionStorage.setItem(STORAGE_PREFIX + NOTIFICATION.id, "1");
    } catch {
      /* ignore */
    }
  };

  const visible = open || forceShow;

  return (
    <>
      {visible && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 animate-in fade-in duration-200"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notif-title"
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close notification"
            onClick={handleClose}
            className="absolute inset-0 bg-black/50 cursor-default border-none outline-none"
          />

          {/* Card */}
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl p-6 animate-in zoom-in-95 duration-200">
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="absolute top-3 right-3 p-1.5 rounded-md text-[#98a1b2] hover:text-[#344054] hover:bg-[#f2f4f7] transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>

            <h2
              id="notif-title"
              className="text-lg font-semibold text-[#1d2639] font-['Inter'] pr-6"
            >
              {customContent?.title || NOTIFICATION.title}
            </h2>

            <p className="mt-2 text-sm text-[#475467] leading-relaxed font-['Inter']">
              {customContent?.message || NOTIFICATION.message}
            </p>

            <div className="mt-6 flex items-center justify-end gap-2">
              {NOTIFICATION.secondary && (
                <a
                  href={NOTIFICATION.secondary.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-md text-sm font-semibold text-[#344054] hover:bg-[#f2f4f7] transition-colors font-['Inter']"
                >
                  {NOTIFICATION.secondary.label}
                </a>
              )}
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2.5 rounded-md bg-[#e35157] hover:bg-[#cf464c] transition-colors text-white text-sm font-semibold font-['Inter']"
              >
                {NOTIFICATION.ctaLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dev-only preview toggle — tree-shaken from production builds */}
      {process.env.NODE_ENV === "development" && (
        <button
          type="button"
          onClick={() => setForceShow((v) => !v)}
          className="fixed bottom-4 left-4 z-[60] px-3 py-2 rounded-md bg-[#1d2639] text-white text-xs font-semibold shadow-lg hover:bg-[#344054] transition-colors font-['Inter']"
          title="Dev only — toggle notification preview"
        >
          👁 {forceShow ? "Hide" : "Show"} notification (preview)
        </button>
      )}
    </>
  );
};

export default NotificationPopup;
